import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

export default function Header() {
  const [timezone, setTimezone] = useState(() => {
    // Check if user has a saved timezone preference
    const savedTimezone = localStorage.getItem('crocus_timezone');
    if (savedTimezone) {
      return savedTimezone;
    }
    
    // Auto-detect user's timezone on first load
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return detectedTimezone || 'UTC';
    } catch {
      return 'UTC';
    }
  });
  
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customTimezone, setCustomTimezone] = useState('');
  const [customTimezoneError, setCustomTimezoneError] = useState('');

  useEffect(() => {
    localStorage.setItem('crocus_timezone', timezone);
    // Dispatch custom event for timezone change
    window.dispatchEvent(new CustomEvent('timezoneChange', { detail: timezone }));
  }, [timezone]);

  const timezones = [
    { id: 'UTC', label: 'UTC' },
    // United States - Central (Chicago first)
    { id: 'America/Chicago', label: 'Chicago (CST/CDT)' },
    // United States - Alphabetical order
    { id: 'America/Atlanta', label: 'Atlanta (EST/EDT)' },
    { id: 'America/Boston', label: 'Boston (EST/EDT)' },
    { id: 'America/Dallas', label: 'Dallas (CST/CDT)' },
    { id: 'America/Denver', label: 'Denver (MST/MDT)' },
    { id: 'America/Detroit', label: 'Detroit (EST/EDT)' },
    { id: 'America/Houston', label: 'Houston (CST/CDT)' },
    { id: 'America/Indianapolis', label: 'Indianapolis (EST/EDT)' },
    { id: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
    { id: 'America/Miami', label: 'Miami (EST/EDT)' },
    { id: 'America/New_York', label: 'New York (EST/EDT)' },
    { id: 'America/Salt_Lake_City', label: 'Salt Lake City (MST/MDT)' },
    { id: 'America/San_Diego', label: 'San Diego (PST/PDT)' },
    { id: 'America/Seattle', label: 'Seattle (PST/PDT)' },
    { id: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
    // International - Alphabetical
    { id: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { id: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
    { id: 'Europe/London', label: 'London (GMT/BST)' },
    { id: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    // Custom option
    { id: 'custom', label: '➕ Custom Timezone...' }
  ];
  
  // If current timezone is not in the predefined list (custom timezone), add it
  const displayTimezones = [...timezones];
  const isCustomTimezone = timezone !== 'custom' && !timezones.find(tz => tz.id === timezone);
  if (isCustomTimezone) {
    // Insert custom timezone before the "Custom Timezone..." option
    displayTimezones.splice(displayTimezones.length - 1, 0, {
      id: timezone,
      label: `${timezone} (Custom)`
    });
  }
  
  const handleTimezoneChange = (evt) => {
    const value = evt.target.value;
    if (value === 'custom') {
      setCustomDialogOpen(true);
    } else {
      setTimezone(value);
    }
  };
  
  const handleCustomTimezoneSubmit = () => {
    // Validate timezone
    try {
      Intl.DateTimeFormat(undefined, { timeZone: customTimezone });
      setTimezone(customTimezone);
      setCustomTimezoneError('');
      setCustomDialogOpen(false);
      setCustomTimezone('');
    } catch (error) {
      setCustomTimezoneError('Invalid timezone. Please enter a valid IANA timezone identifier.');
    }
  };
  
  const handleCustomDialogClose = () => {
    setCustomDialogOpen(false);
    setCustomTimezone('');
    setCustomTimezoneError('');
  };

  return (
    <>
      <div className="w-full border-b bg-white sticky top-0 z-50 shadow-sm">
        {/* Desktop header - single row */}
        <div className="hidden md:flex max-w-7xl mx-auto px-4 h-14 items-center justify-between">
          <div className="flex items-center">
            <a
              href="https://crocus-urban.org"
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline flex-shrink-0"
            >
              <img
                src="/CROCUS-Logo-1-1024x131.png"
                alt="CROCUS Logo"
                className="h-5"
              />
            </a>
            <div className="mx-3 h-6 w-px bg-gray-200" />
            <div className="flex items-center gap-6">
              <NavLink
                to="/locations"
                className={({ isActive }) =>
                  `flex items-center ${isActive ? "text-blue-600 font-medium" : "text-gray-800"}`
                }
              >
                Locations
              </NavLink>
              <NavLink
                to="/query-browser"
                className={({ isActive }) =>
                  `flex items-center ${isActive ? "text-blue-600 font-medium" : "text-gray-800"}`
                }
              >
                Data
              </NavLink>
              <NavLink
                to="/devices"
                className={({ isActive }) =>
                  `flex items-center ${isActive ? "text-blue-600 font-medium" : "text-gray-800"}`
                }
              >
                Devices
              </NavLink>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel id="header-timezone-label" style={{ fontSize: '14px' }}>Timezone</InputLabel>
              <Select
                labelId="header-timezone-label"
                id="header-timezone"
                value={timezone}
                onChange={handleTimezoneChange}
                label="Timezone"
                style={{ height: '32px', fontSize: '14px' }}
              >
                {displayTimezones.map(tz =>
                  <MenuItem value={tz.id} key={tz.id} style={{ fontSize: '14px' }}>{tz.label}</MenuItem>
                )}
              </Select>
            </FormControl>
          </div>
        </div>
        
        {/* Mobile header - two rows */}
        <div className="md:hidden">
          <div className="flex items-center justify-between px-3 py-2">
            <a
              href="https://crocus-urban.org"
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline flex-shrink-0"
            >
              <img
                src="/CROCUS-Logo-1-1024x131.png"
                alt="CROCUS Logo"
                className="h-4"
                style={{ minWidth: '100px' }}
              />
            </a>
            <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
              <InputLabel id="header-timezone-label-mobile" style={{ fontSize: '12px' }}>Timezone</InputLabel>
              <Select
                labelId="header-timezone-label-mobile"
                id="header-timezone-mobile"
                value={timezone}
                onChange={handleTimezoneChange}
                label="Timezone"
                style={{ height: '28px', fontSize: '12px' }}
              >
                {displayTimezones.map(tz =>
                  <MenuItem value={tz.id} key={tz.id} style={{ fontSize: '12px' }}>{tz.label}</MenuItem>
                )}
              </Select>
            </FormControl>
          </div>
          <div className="flex items-center gap-4 px-3 pb-2 border-t pt-2 text-sm">
            <NavLink
              to="/locations"
              className={({ isActive }) =>
                `${isActive ? "text-blue-600 font-medium" : "text-gray-800"}`
              }
            >
              Locations
            </NavLink>
            <NavLink
              to="/query-browser"
              className={({ isActive }) =>
                `${isActive ? "text-blue-600 font-medium" : "text-gray-800"}`
              }
            >
              Data
            </NavLink>
            <NavLink
              to="/devices"
              className={({ isActive }) =>
                `${isActive ? "text-blue-600 font-medium" : "text-gray-800"}`
              }
            >
              Devices
            </NavLink>
          </div>
        </div>
      </div>
      
      {/* Custom Timezone Dialog */}
      <Dialog open={customDialogOpen} onClose={handleCustomDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Enter Custom Timezone</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600 mb-3">
            Enter a valid IANA timezone identifier. You can find a list of timezones at{' '}
            <a 
              href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Wikipedia
            </a>.
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Examples: America/Phoenix, Europe/Amsterdam, Asia/Seoul
          </p>
          <TextField
            autoFocus
            fullWidth
            label="IANA Timezone Identifier"
            value={customTimezone}
            onChange={(e) => setCustomTimezone(e.target.value)}
            placeholder="e.g., America/Phoenix"
            error={!!customTimezoneError}
            helperText={customTimezoneError}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCustomTimezoneSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomDialogClose}>Cancel</Button>
          <Button onClick={handleCustomTimezoneSubmit} variant="contained" color="primary">
            Set Timezone
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}